import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import Seo from '../components/Seo';
import ContentModule from '../components/ContentModule';
import { SITE_URL } from '../seoConfig';
import { toTitleCase } from '../utils/format';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => Number(n || 0).toLocaleString();

const PhotographerCard = ({ p }) => {
  const name = toTitleCase(p.user_display_name || 'Photographer');
  const profileUrl = p.user_url || '#';
  const avatar = p.user_image_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&size=300`;
  return (
    <article
      className="photographer-card flex flex-col items-center text-center bg-white rounded-xl border border-gray-100 p-6 hover:shadow-md transition-shadow"
      data-testid={`photographer-card-${p.user_id}`}
    >
      <Link to={profileUrl} className="photographer-main-link block">
        <img
          src={avatar}
          alt={`${name} photographer on Explora Mauritius`}
          className="w-[150px] h-[150px] rounded-full object-cover mx-auto"
          loading="lazy"
        />
        <h3 className="mt-4 text-lg font-semibold text-gray-900" data-testid={`photographer-name-${p.user_id}`}>
          {name}
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          {fmt(p.photo_count)} {p.photo_count === 1 ? 'photo' : 'photos'} · {fmt(p.total_views)} views
        </p>
      </Link>

      {p.top_tags && p.top_tags.length > 0 && (
        <div className="photographer-tags mt-3 flex flex-wrap items-center justify-center gap-2">
          {p.top_tags.map((t) => (
            <Link
              key={t.cat_id}
              to={`/photos-hd/${t.slug}`}
              className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded transition-colors"
              data-testid={`photographer-tag-${p.user_id}-${t.cat_id}`}
            >
              {t.name}
            </Link>
          ))}
        </div>
      )}

      <Link
        to={profileUrl}
        className="view-profile mt-4 inline-block text-sm font-medium text-gray-700 hover:text-gray-900 hover:underline"
        data-testid={`photographer-view-profile-${p.user_id}`}
      >
        View profile
      </Link>
    </article>
  );
};

const PhotographersListPage = () => {
  const [photographers, setPhotographers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/photographers`);
        if (!cancelled) setPhotographers(data.photographers || []);
      } catch (err) {
        console.error('Failed to load photographers:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const featured = useMemo(() => {
    return [...photographers]
      .sort((a, b) => (b.total_views || 0) - (a.total_views || 0))
      .slice(0, 6);
  }, [photographers]);

  const browseAll = useMemo(() => {
    return [...photographers].sort((a, b) => {
      const an = (a.user_display_name || '').toLowerCase();
      const bn = (b.user_display_name || '').toLowerCase();
      return an.localeCompare(bn);
    });
  }, [photographers]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="photographers-list-page">
      <Seo
        title="Photographers of Mauritius"
        description={`${photographers.length} talented local photographers sharing high-resolution Mauritius photography.`}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Photographers of Mauritius',
            url: `${SITE_URL}/photographers-in-mauritius`,
            description: 'Local photographers sharing their Mauritius photography on Explora.',
            isPartOf: { '@type': 'WebSite', name: 'Explora Mauritius', url: SITE_URL },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: 'Photographers', item: `${SITE_URL}/photographers-in-mauritius` },
            ],
          },
        ]}
      />

      <div className="bg-gray-50 border-b border-gray-200 py-10">
        <div className="max-w-full md:max-w-[70%] mx-auto px-4 md:px-4 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2" data-testid="photographers-list-h1">
            Photographers of Mauritius
          </h1>
          <p className="text-gray-600">
            {photographers.length} talented {photographers.length === 1 ? 'photographer' : 'photographers'} sharing their work
          </p>
          <ContentModule name="photographers" />
        </div>
      </div>

      <div className="max-w-full md:max-w-[70%] mx-auto px-4 md:px-4 lg:px-8 py-10 space-y-12">
        {/* Featured photographers */}
        {featured.length > 0 && (
          <section data-testid="photographers-featured-section">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured photographers</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featured.map((p) => (
                <PhotographerCard key={`featured-${p.user_id}`} p={p} />
              ))}
            </div>
          </section>
        )}

        {/* Browse All */}
        {browseAll.length > 0 && (
          <section data-testid="photographers-browse-section">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Browse all photographers</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {browseAll.map((p) => (
                <PhotographerCard key={`all-${p.user_id}`} p={p} />
              ))}
            </div>
          </section>
        )}

        {photographers.length === 0 && (
          <p className="text-center text-gray-500 py-12">No photographers yet.</p>
        )}
      </div>
    </div>
  );
};

export default PhotographersListPage;
